import { Component, inject, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SearchStateService } from '../../shared/services/search-state.service';
import { MCAppGatewayService } from '../../../shared/services/mcappgateway/mcappgateway.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { LocationSearchDetailsProviderCardComponent } from '../location-search-details-provider-card/location-search-details-provider-card.component';
import { TranslateService } from '@ngx-translate/core';
import { LocationOpenHour } from '../../../shared/model/providerlocationsearch/SearchResult';
import { GetCareNowFormatService } from '../../../get-care-now-feature/utilities/getcarenow-format.service';
import { MonitoringService } from '../../../shared/services/monitoring/monitoring-service';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-location-search-details',
  imports: [CommonModule, MatProgressSpinnerModule, LocationSearchDetailsProviderCardComponent],
  templateUrl: './location-search-details.component.html',
  styleUrl: './location-search-details.component.css'
})
export class LocationSearchDetailsComponent {
  mcAppGateway = inject(MCAppGatewayService);
  searchState = inject(SearchStateService);

  @Input() locale: string = '';
  @Input() currentLocation: any = null; 
  @Input() currentCityOrZip: string = "";
  @Input() fromProviderSearch: boolean = false;
  @Input() providerDetails: any = null;

  locationDetails$ = this.searchState.locationDetails$;
  providerDetailsLocation$ = this.searchState.providerDetailsLocation$;
  showSpinner$ = this.searchState.showSpinner$;

  showSpinner: boolean = false;
  details: any = null;
  waitTime: string = '';
  providerList: any[] = [];
  isIPUC: boolean = false;
  groupedOpenHours: Map<string, LocationOpenHour[]> = new Map();

  breadcrumbText = "Location Search";
  breadcrumbText2 = "Search Results";
  providersLabel = "";
  milesAwayText: string = 'DESCRIPTION';
  getDirectionsLabel : string = 'GET_DIRECTIONS';
  soonAsText: string = 'BOOK_AS_SOON_AS';
  walkInWait: string = 'WALK_IN_WAIT';
  reserveMySpotBtnText: string = 'RESERVE_MY_SPOT';

  private getCareNowFormatService: GetCareNowFormatService;

  constructor(private translate: TranslateService, private monitoringService: MonitoringService) {
    this.translate.use(sessionStorage.getItem('locale') || 'en-US'); // Default to 'en-US' if no locale is provided
    this.getCareNowFormatService = new GetCareNowFormatService(this.translate);
  }

  ngOnInit(): void {
    console.log("LocationSearchDetailsComponent initialized");

    this.showSpinner$?.subscribe(show => {
      this.showSpinner = show;
    });

    if (this.fromProviderSearch === true)  {
      this.translate.get('PROVIDER_SEARCH').subscribe((translated: string) => {
        this.breadcrumbText = translated;
      });
    } 
    else {
      this.translate.get('LOCATION_SEARCH').subscribe((translated: string) => {
        this.breadcrumbText = translated;
      });
    }
  
    this.translate.get('RESULTS').subscribe((translated: string) => {
      this.breadcrumbText2 = translated;
    });
    
    this.translate.get('LOCATION_PROFILE.PROVIDERS').subscribe(translated => {
      this.providersLabel = translated;
    });

    this.translate.get('MILES_AWAY').subscribe((translated: string) => {
        this.milesAwayText = translated;
    });

    this.translate.get('GET_DIRECTIONS').subscribe((translated: string) => {
      this.getDirectionsLabel = translated;
    });

    this.translate.get('BOOK_AS_SOON_AS').subscribe((translated: string) => {
      this.soonAsText = translated;
    });

    this.translate.get('WALK_IN_WAIT').subscribe((translated: string) => {
      this.walkInWait = translated;
    });

    this.translate.get('RESERVE_MY_SPOT').subscribe((translated: string) => {
      this.reserveMySpotBtnText = translated;
    });

    this.locationDetails$?.subscribe(details => {
      if (details) {
        this.details = details;
        this.providerList = details.providers || []; // Each item should match LocationDetails

        console.log("Loaded location details:", this.details);

        this.waitTime = this.getCareNowFormatService.formatWaitTime(this.details.waitTime);
        this.isIPUC = ((this.details.types ?? "").toUpperCase().includes("URGENT CARE") && this.details.departmentId != null && this.details.departmentId !== "");
        console.log("Is IPUC Location:", this.isIPUC);

        // Group open hours by type
        if (details.locationOpenHours && Array.isArray(details.locationOpenHours)) {
          console.log("Location Open Hours:", details.locationOpenHours);
          this.groupOpenHoursByType(details.locationOpenHours);
        }
      }
    });
  }

  reserveNow () {
    const eptID = sessionStorage.getItem("eptID");

    if (eptID == null || eptID == undefined || eptID == "") {
      this.reserveNowUnAuth();
    }
    else {
      this.reserveNowAuth();
    }
  }

  reserveNowUnAuth() {
    var deepLink = environment.web_unauth_omw_deep_link_base_url + this.details.departmentId;

    this.monitoringService.logEvent('Schedule/NoAuth', { feature: 'search', subfeature: 'location-search', platform: 'web' }); // log as web event
    
    console.log("reserve now clicked (IPUC)");
    console.log(deepLink);

    window.location.href = deepLink;
  }
  
  reserveNowAuth() {
    var deepLink = environment.web_omw_deep_link_base_url + this.details.departmentId; // For web users, use the web deep link
    
    console.log("reserve now clicked (IPUC)");
    console.log(deepLink);
  
    this.monitoringService.logEvent('Schedule', { feature: 'search', subfeature: 'location-search', platform: 'web' }); // log as web event
    window.location.href = deepLink;
  }

  groupOpenHoursByType(openHours: LocationOpenHour[]): void {
    this.groupedOpenHours.clear();
    
    openHours.forEach(hour => {
      const type = hour.type || 'General';
      if (!this.groupedOpenHours.has(type)) {
        this.groupedOpenHours.set(type, []);
      }
      this.groupedOpenHours.get(type)?.push(hour);
    });
  }

  getOpenHoursTypes(): string[] {
    return Array.from(this.groupedOpenHours.keys());
  }

  getOpenHoursByType(type: string): LocationOpenHour[] {
    return this.groupedOpenHours.get(type) || [];
  }

  onGetDirections() {
    if (this.details && this.details.locationDirection) {
      window.open(this.details.locationDirection, '_blank');
    }
  }

  returnToSearchResults() {
    if (this.fromProviderSearch === true) { // Location Detail view from Provider Search
      this.searchState.clearCurrentProviderDetailsLocation();
      this.searchState.clearCurrentProviderDetails();
    }
    else {
      // Logic to return to search results view
      this.searchState.clearCurrentLocationDetails();
    }
  }

  clearSearch(): void {
    if (this.fromProviderSearch === true) { // Location Detail view from Provider Search
      this.searchState.setProviderSearchFilterToggle(false);
      this.searchState.setProviderSearchQuery({ nameOrSpecialty: "", cityOrZip: this.currentCityOrZip })
      this.searchState.clearProviderSearchFilters();
      this.searchState.clearProviderSearchResults();
      this.searchState.clearCurrentProviderDetails();
      this.searchState.clearCurrentLocationDetails();
      this.searchState.clearCurrentProviderDetailsLocation();
    }
    else {  
      this.searchState.setLocationSearchFilterToggle(false);
      this.searchState.setLocationSearchQuery({ facilityOrLocationName: "", cityOrZip: this.currentCityOrZip })
      this.searchState.clearLocationSearchFilters();
      this.searchState.clearLocationSearchResults();   
      this.searchState.clearCurrentLocationDetails();
    }
  }

  returnToProviderDetails() {
    // Logic to return to provider details view
    this.searchState.clearCurrentProviderDetailsLocation();
  }
}